
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTimer } from '../context/TimerContext';
import { formatTime } from '../utils/timerUtils';
import { getMediaUrl } from '../utils/mediaUtils';
import { TIMER_MEDIA_OBJECT_FIT, getTimerMediaObjectFitClass } from '../utils/timerMediaFit';
import { getTimerUpNextDisplay, resolveTimerUpNextPhase } from '../utils/timerUpNext';
import { ImageKitProvider } from '@imagekit/react';
import { ArrowLeft, ClipboardList, Dumbbell, Pause, Play, SkipForward, TimerReset } from 'lucide-react';
import config from '../config/config';
import coinSound from '../components/sounds/coin.mp3';
import restChimeSound from '../components/sounds/rest-chime.wav';
import FormCheckPanel from './FormCheckPanel';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useLocale } from '../context/LocaleContext';
import useWakeLock from '../hooks/useWakeLock';
import { getAiFeatureQuotas, getImagesForQuest } from '../api/dataService';
import { getMediaUnavailableText } from '../utils/mediaAvailability';
import {
    AI_FEATURE_KEYS,
    getFeatureQuota,
    getQuotaMessage,
    isQuotaBlocked
} from '../utils/aiFeatureQuotas';

const normalizeExerciseName = (value) => String(value || '').trim().toLowerCase();

const buildExerciseMediaLookup = (questLike) => {
    const lookup = {};
    const exercises = questLike?.detailedDescription?.exercises;
    if (!Array.isArray(exercises)) {
        return lookup;
    }

    exercises.forEach((exercise) => {
        const key = normalizeExerciseName(exercise?.name);
        if (!key) return;

        const imageUrl = typeof exercise?.imageUrl === 'string' ? exercise.imageUrl : '';
        const videoUrl = typeof exercise?.videoUrl === 'string' ? exercise.videoUrl : '';
        const posterUrl = typeof exercise?.posterUrl === 'string' ? exercise.posterUrl : '';

        if (!imageUrl && !videoUrl && !posterUrl) {
            return;
        }

        lookup[key] = { imageUrl, videoUrl, posterUrl };
    });

    return lookup;
};

const questNeedsMediaHydration = (questLike) => {
    const exercises = questLike?.detailedDescription?.exercises;
    if (!Array.isArray(exercises) || exercises.length === 0) {
        return false;
    }
    return exercises.some((exercise) => !exercise?.videoUrl && !exercise?.imageUrl);
};

const buildImageSourceCandidates = (rawValue, imagekitEndpoint, workerBase) => {
    const value = String(rawValue || '').trim();
    if (!value) return [];

    if (/^(https?:\/\/|blob:|data:)/i.test(value)) {
        return [value];
    }

    const normalizedPath = value.replace(/^\/+/, '');
    const imagekitBase = String(imagekitEndpoint || '').trim().replace(/\/$/, '');
    const workerBaseNormalized = String(workerBase || '').trim().replace(/\/$/, '');

    const imagekitUrl = imagekitBase ? `${imagekitBase}/${normalizedPath}` : '';
    const workerUrl = workerBaseNormalized ? `${workerBaseNormalized}/${normalizedPath}` : '';
    const likelyImageKitPath = normalizedPath.startsWith('forza_quest_images/');
    const ordered = likelyImageKitPath
        ? [imagekitUrl, workerUrl]
        : [workerUrl, imagekitUrl];

    const unique = [];
    ordered.forEach((url) => {
        if (!url || unique.includes(url)) return;
        unique.push(url);
    });
    return unique;
};

const MediaUnavailablePlaceholder = () => {
    const { t } = useLocale();
    return (
        <div className="flex h-full w-full items-center justify-center px-4 text-center text-sm font-semibold text-gray-300">
            {getMediaUnavailableText(t)}
        </div>
    );
};

const TimerStatBox = ({ label, value }) => (
    <div className="flex min-h-[72px] flex-1 flex-col items-center justify-center rounded-xl border border-white/15 bg-slate-950/45 px-3 py-3 text-center shadow-2xl backdrop-blur-xl">
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-100/70">{label}</span>
        <span className="mt-1 max-w-full truncate text-lg font-black leading-tight text-white sm:text-2xl">{value}</span>
    </div>
);

const RestPhaseBackground = () => {
    const { t } = useLocale();
    return (
        <div className="absolute inset-0 bg-[#111827]">
            <div className="absolute inset-0 opacity-60 [background:repeating-linear-gradient(135deg,rgba(34,211,238,0.16)_0px,rgba(34,211,238,0.16)_1px,transparent_1px,transparent_18px)]" />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950/10 via-slate-950/55 to-slate-950/95" />
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-44 w-44 items-center justify-center rounded-full border border-cyan-200/25 bg-slate-950/45 text-center shadow-2xl backdrop-blur-md">
                    <div>
                        <TimerReset className="mx-auto h-9 w-9 text-cyan-200" />
                        <p className="mt-3 text-sm font-black uppercase tracking-[0.35em] text-cyan-100">{t('timerSession.rest', 'Rest')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const WorkoutTrackerSheet = ({ isOpen, workoutPerformance, onUpdate, onClose }) => {
    const { t } = useLocale();
    if (!isOpen) return null;

    const exercises = Array.isArray(workoutPerformance?.exercises) ? workoutPerformance.exercises : [];

    return (
        <div className="fixed inset-0 z-[70] flex items-end bg-slate-950/50 backdrop-blur-sm" role="dialog" aria-modal="true">
            <div className="max-h-[82dvh] w-full overflow-hidden rounded-t-[1.75rem] border border-white/10 bg-slate-950/95 text-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
                    <div>
                        <h3 className="text-xl font-black tracking-tight">{t('timerSession.workoutTracker', 'Workout Tracker')}</h3>
                        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-100/70">{t('timerSession.timerPaused', 'Timer paused')}</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-xl bg-cyan-400 px-5 py-2 text-sm font-black text-slate-950 transition hover:bg-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
                    >
                        {t('timerSession.done', 'Done')}
                    </button>
                </div>

                <div className="max-h-[calc(82dvh-81px)] overflow-y-auto px-5 py-4">
                    {exercises.length === 0 ? (
                        <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200">
                            {t('timerSession.noTrackableExercises', 'No trackable exercises are available for this quest.')}
                        </div>
                    ) : (
                        <div className="space-y-5">
                            {exercises.map((exercise) => (
                                <section key={`${exercise.exerciseIndex}-${exercise.name}`} className="rounded-xl border border-white/10 bg-white/[0.06] p-4">
                                    <div className="mb-3 flex items-start justify-between gap-3">
                                        <div>
                                            <h4 className="text-base font-black text-white">{exercise.name}</h4>
                                            <p className="mt-1 text-xs font-semibold text-slate-300">
                                                {t('timerSession.targetValue', 'Target {value}', { value: exercise.targetReps || 'N/A' })}
                                                {exercise.plannedSuggestedWeight ? ` - ${exercise.plannedSuggestedWeight}` : ''}
                                            </p>
                                        </div>
                                        <Dumbbell className="mt-0.5 h-5 w-5 shrink-0 text-cyan-200" />
                                    </div>

                                    <div className="space-y-3">
                                        {exercise.sets.map((set) => (
                                            <div key={`${exercise.exerciseIndex}-${set.setNumber}`} className="grid grid-cols-[64px_minmax(0,1fr)_minmax(0,1fr)] items-end gap-3">
                                                <div className="pb-2 text-sm font-black text-cyan-100">{t('timerSession.setNumber', 'Set {number}', { number: set.setNumber })}</div>
                                                <label className="block">
                                                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t('timerSession.reps', 'Reps')}</span>
                                                    <input
                                                        type="text"
                                                        inputMode="numeric"
                                                        value={set.actualReps || ''}
                                                        onChange={(event) => onUpdate(exercise.exerciseIndex, set.setNumber, { actualReps: event.target.value })}
                                                        className="h-11 w-full rounded-xl border border-white/10 bg-slate-900 px-3 text-base font-bold text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-300"
                                                        placeholder={set.targetReps || '0'}
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className="mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">{t('timerSession.weightUnit', 'Weight {unit}', { unit: set.weightUnit || workoutPerformance.weightUnit })}</span>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={set.actualWeight || ''}
                                                        onChange={(event) => onUpdate(exercise.exerciseIndex, set.setNumber, { actualWeight: event.target.value })}
                                                        className="h-11 w-full rounded-xl border border-white/10 bg-slate-900 px-3 text-base font-bold text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-300"
                                                        placeholder="0"
                                                    />
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const TimerSessionModal = ({ isOpen, onClose, quest, onComplete }) => {
    const {
        status,
        timeRemaining,
        phases,
        currentPhaseIndex,
        startTimer,
        pauseTimer,
        resumeTimer,
        skipPhase,
        resetTimer,
        workoutPerformance,
        updateWorkoutPerformance,
        isSubmitting,
        error,
        addVerificationListener,
    } = useTimer();
    const coinAudioRef = useRef(null);
    const restAudioRef = useRef(null);
    const hasStartedRef = useRef(false);
    const previousPhaseIndexRef = useRef(-1);
    const { user } = useAuth();
    const { addToast } = useToast();
    const { t } = useLocale();
    const [isFormCheckOpen, setIsFormCheckOpen] = useState(false);
    const resumeAfterFormCheckRef = useRef(false);
    const [isTrackerOpen, setIsTrackerOpen] = useState(false);
    const resumeAfterTrackerRef = useRef(false);
    const completionHandledRef = useRef(false);
    const [wakeLockFailed, setWakeLockFailed] = useState(false);
    const [exerciseMediaLookup, setExerciseMediaLookup] = useState({});
    const [aiFeatureQuotas, setAiFeatureQuotas] = useState(null);

    const handleWakeLockError = useCallback((err) => {
        console.warn('[Timer] Wake lock unavailable', err);
        addToast(t('timerSession.wakeLockUnavailable', 'Screen wake lock unavailable; your device may sleep during the timer.'), 'error');
    }, [addToast, t]);

    const {
        request: requestWakeLock,
        release: releaseWakeLock,
        unavailableReason: wakeLockErrorMessage,
    } = useWakeLock({ onError: handleWakeLockError });

    const currentPhase = useMemo(() => phases[currentPhaseIndex], [phases, currentPhaseIndex]);
    const subscriptionPlanRaw = user?.subscriptionPlan || 'free-tier';
    const subscriptionPlan = typeof subscriptionPlanRaw === 'string' ? subscriptionPlanRaw.toLowerCase() : 'free-tier';
    const canShowFormCheck = currentPhase?.type === 'work' && ['ultra'].includes(subscriptionPlan);
    const formCheckQuota = getFeatureQuota(aiFeatureQuotas, AI_FEATURE_KEYS.FORM_CHECK);
    const formCheckBlocked = isQuotaBlocked(formCheckQuota);
    const formCheckQuotaMessage = getQuotaMessage(formCheckQuota);
    const formCheckExerciseName = currentPhase?.exerciseName || quest?.name || t('timerSession.workout', 'Workout');
    const formCheckExerciseDescription = currentPhase?.instructions
        || quest?.detailedDescription?.summary
        || '';
    const phaseMetrics = useMemo(() => {
        if (!currentPhase || currentPhase.type !== 'work') {
            return null;
        }

        const restDisplay = Number.isFinite(currentPhase.rest)
            ? t('timerSession.seconds', '{count} seconds', { count: currentPhase.rest })
            : currentPhase.rest || 'N/A';

        return {
            sets: Number.isFinite(currentPhase.totalSets) ? currentPhase.totalSets : currentPhase.sets || 'N/A',
            reps: currentPhase.reps || 'N/A',
            rest: restDisplay,
            suggestedWeight: currentPhase.suggestedWeight || 'N/A'
        };
    }, [currentPhase]);

    const instructionSteps = useMemo(() => {
        if (!currentPhase?.instructions) return [];
        const raw = String(currentPhase.instructions).trim();
        if (!raw) return [];

        let steps = raw
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean);

        if (steps.length <= 1) {
            steps = raw
                .replace(/^\s*-\s*/, '')
                .split(/\s+-\s+/)
                .map(line => line.trim())
                .filter(Boolean);
        }

        return steps.map(line => line.replace(/^-+\s*/, '').trim()).filter(Boolean);
    }, [currentPhase?.instructions]);

    const resolvedPhaseMedia = useMemo(() => {
        if (!currentPhase) {
            return { exerciseImage: '', exerciseVideo: '', exercisePoster: '' };
        }
        const fallbackMedia = exerciseMediaLookup[normalizeExerciseName(currentPhase.exerciseName)] || {};
        return {
            exerciseImage: currentPhase.exerciseImage || fallbackMedia.imageUrl || '',
            exerciseVideo: currentPhase.exerciseVideo || fallbackMedia.videoUrl || '',
            exercisePoster: currentPhase.exercisePoster || fallbackMedia.posterUrl || ''
        };
    }, [currentPhase, exerciseMediaLookup]);

    const phaseExerciseImage = typeof resolvedPhaseMedia.exerciseImage === 'string' ? resolvedPhaseMedia.exerciseImage : '';
    const phaseExerciseVideo = typeof resolvedPhaseMedia.exerciseVideo === 'string' ? resolvedPhaseMedia.exerciseVideo : '';
    const phaseExercisePoster = typeof resolvedPhaseMedia.exercisePoster === 'string' ? resolvedPhaseMedia.exercisePoster : '';
    const phaseStillImage = phaseExerciseImage || phaseExercisePoster;
    const phaseImageCandidates = useMemo(
        () => buildImageSourceCandidates(phaseStillImage, config.imagekit.urlEndpoint, config.media.workerBase),
        [phaseStillImage]
    );
    const [phaseImageCandidateIndex, setPhaseImageCandidateIndex] = useState(0);
    const [phaseVideoFailed, setPhaseVideoFailed] = useState(false);
    const [phaseImageFitClass, setPhaseImageFitClass] = useState(TIMER_MEDIA_OBJECT_FIT.COVER);
    const [phaseVideoFitClass, setPhaseVideoFitClass] = useState(TIMER_MEDIA_OBJECT_FIT.COVER);
    const phaseImageSrc = phaseImageCandidates[phaseImageCandidateIndex] || '';

    useEffect(() => {
        setPhaseImageCandidateIndex(0);
    }, [phaseStillImage]);

    useEffect(() => {
        setPhaseImageFitClass(TIMER_MEDIA_OBJECT_FIT.COVER);
    }, [phaseImageSrc]);

    useEffect(() => {
        setPhaseVideoFailed(false);
        setPhaseVideoFitClass(TIMER_MEDIA_OBJECT_FIT.COVER);
    }, [phaseExerciseVideo]);

    const handlePhaseImageError = useCallback(() => {
        setPhaseImageCandidateIndex((currentIndex) => {
            const nextIndex = currentIndex + 1;
            return nextIndex < phaseImageCandidates.length ? nextIndex : currentIndex;
        });
    }, [phaseImageCandidates.length]);

    const handlePhaseImageLoad = useCallback((event) => {
        const image = event.currentTarget;
        setPhaseImageFitClass(getTimerMediaObjectFitClass({
            width: image.naturalWidth,
            height: image.naturalHeight
        }));
    }, []);

    const handlePhaseVideoLoadedMetadata = useCallback((event) => {
        const video = event.currentTarget;
        setPhaseVideoFitClass(getTimerMediaObjectFitClass({
            width: video.videoWidth,
            height: video.videoHeight
        }));
    }, []);

    const nextWorkPhase = useMemo(() => {
        return resolveTimerUpNextPhase({ phases, currentPhaseIndex });
    }, [currentPhaseIndex, phases]);
    const upNextDisplay = useMemo(() => getTimerUpNextDisplay(nextWorkPhase), [nextWorkPhase]);

    const currentSetLabel = currentPhase?.type === 'work'
        ? t('timerSession.setProgress', 'Set {current}/{total}', {
            current: currentPhase.setNumber || 1,
            total: currentPhase.totalSets || 1,
        })
        : t('timerSession.rest', 'Rest');
    const targetRepsLabel = currentPhase?.type === 'work'
        ? (currentPhase.reps || 'N/A')
        : (nextWorkPhase?.type === 'work' ? (nextWorkPhase.reps || t('timerSession.next', 'Next')) : t('timerSession.next', 'Next'));
    const exerciseTitle = currentPhase?.type === 'rest'
        ? t('timerSession.rest', 'Rest')
        : (currentPhase?.exerciseName || quest?.name || t('timerSession.workout', 'Workout'));
    const descriptionText = instructionSteps.length > 0
        ? instructionSteps.join(' / ')
        : (currentPhase?.instructions || quest?.description || '');
    const canUsePhaseVideo = currentPhase?.type !== 'rest' && phaseExerciseVideo && !phaseVideoFailed;

    useEffect(() => {
        if (isOpen && quest) {
            startTimer(quest);
        }
    }, [isOpen, quest, startTimer]);

    const refreshAiFeatureQuotas = useCallback(() => {
        if (!isOpen || !quest) return Promise.resolve();
        return getAiFeatureQuotas()
            .then((response) => {
                setAiFeatureQuotas(response.data || null);
            })
            .catch((err) => {
                console.warn('[Timer] Failed to refresh AI feature quotas', err);
                setAiFeatureQuotas(null);
            });
    }, [isOpen, quest]);

    useEffect(() => {
        if (!isOpen || !quest) {
            setAiFeatureQuotas(null);
            return undefined;
        }

        let cancelled = false;
        getAiFeatureQuotas()
            .then((response) => {
                if (!cancelled) {
                    setAiFeatureQuotas(response.data || null);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.warn('[Timer] Failed to fetch AI feature quotas', err);
                    setAiFeatureQuotas(null);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, quest]);

    useEffect(() => {
        if (!isOpen || !quest) {
            setExerciseMediaLookup({});
            return undefined;
        }

        setExerciseMediaLookup(buildExerciseMediaLookup(quest));
        if (!questNeedsMediaHydration(quest) || !quest._id) {
            return undefined;
        }

        let cancelled = false;
        let retryTimeoutId = null;

        const hydrateMedia = async (attempt = 1) => {
            try {
                const response = await getImagesForQuest(quest._id);
                if (cancelled) return;
                const hydratedLookup = buildExerciseMediaLookup(response?.data);
                if (Object.keys(hydratedLookup).length > 0) {
                    setExerciseMediaLookup((prev) => ({ ...prev, ...hydratedLookup }));
                }
            } catch (err) {
                if (cancelled) return;
                if (err?.response?.status === 409 && attempt < 3) {
                    retryTimeoutId = window.setTimeout(() => hydrateMedia(attempt + 1), attempt * 1200);
                    return;
                }
                console.warn('[Timer] Failed to hydrate media for timer quest', err);
            }
        };

        hydrateMedia();

        return () => {
            cancelled = true;
            if (retryTimeoutId) {
                window.clearTimeout(retryTimeoutId);
            }
        };
    }, [isOpen, quest]);

    useEffect(() => {
        // Initialize audio objects once if they don't exist
        if (!coinAudioRef.current) {
            const coin = new Audio(coinSound);
            coin.preload = 'auto';
            coin.volume = 1.0;
            coinAudioRef.current = coin;
        }

        if (!restAudioRef.current) {
            const rest = new Audio(restChimeSound);
            rest.preload = 'auto';
            rest.volume = 1.0;
            restAudioRef.current = rest;
        }

        // Cleanup only on unmount (empty dependency array)
        return () => {
            if (coinAudioRef.current) {
                coinAudioRef.current.pause();
                coinAudioRef.current = null;
            }
            if (restAudioRef.current) {
                restAudioRef.current.pause();
                restAudioRef.current = null;
            }
        };
    }, []);

    // Stop audio when modal closes
    useEffect(() => {
        if (!isOpen) {
            if (coinAudioRef.current) {
                coinAudioRef.current.pause();
                coinAudioRef.current.currentTime = 0;
            }
            if (restAudioRef.current) {
                restAudioRef.current.pause();
                restAudioRef.current.currentTime = 0;
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (status === 'running' && !hasStartedRef.current) {
            hasStartedRef.current = true;
            const coin = coinAudioRef.current;
            if (coin) {
                coin.currentTime = 0;
                const promise = coin.play();
                if (promise?.catch) {
                    promise.catch((err) => console.warn('[Timer] Failed to play start sound', err));
                }
            }
        }
        if (status !== 'running') {
            hasStartedRef.current = false;
            previousPhaseIndexRef.current = -1;
        }
    }, [status]);

    // Play sound on phase change
    useEffect(() => {
        if (status !== 'running') {
            return;
        }

        if (previousPhaseIndexRef.current === -1) {
            previousPhaseIndexRef.current = currentPhaseIndex;
            return;
        }

        if (currentPhaseIndex === previousPhaseIndexRef.current) {
            return;
        }

        previousPhaseIndexRef.current = currentPhaseIndex;

        if (currentPhaseIndex < 0) {
            return;
        }

        const phase = phases[currentPhaseIndex];
        const targetAudio = phase?.type === 'rest' ? restAudioRef.current : coinAudioRef.current;
        if (!targetAudio) {
            return;
        }
        try {
            targetAudio.currentTime = 0;
            const promise = targetAudio.play();
            if (promise?.catch) {
                promise.catch((err) => console.warn('[Timer] Phase sound failed', err));
            }
        } catch (err) {
            console.warn('[Timer] Phase sound error', err);
        }
    }, [currentPhaseIndex, status, phases]);

    // Handle completion
    // REMOVED: Premature closing logic. We now wait for addVerificationListener to fire with the payload.
    // This ensures we have the XP/Level data before closing the modal, allowing the parent to update optimistically.

    useEffect(() => {
        if (!addVerificationListener) return undefined;
        const unsubscribe = addVerificationListener((payload) => {
            if (completionHandledRef.current) return;
            completionHandledRef.current = true;
            onComplete?.(payload);
            onClose();
        });
        return unsubscribe;
    }, [addVerificationListener, onClose, onComplete]);

    const handleClose = useCallback(() => {
        setIsFormCheckOpen(false);
        setIsTrackerOpen(false);
        resumeAfterFormCheckRef.current = false;
        resumeAfterTrackerRef.current = false;
        pauseTimer();
        releaseWakeLock();
        onClose();
    }, [onClose, pauseTimer, releaseWakeLock]);

    const handleOpenFormCheck = useCallback(() => {
        if (!canShowFormCheck) return;
        if (formCheckBlocked) {
            addToast(formCheckQuotaMessage || t('timerSession.formCheckLimitReached', 'Form Check daily limit reached.'), 'error');
            return;
        }
        const shouldResume = status === 'running';
        resumeAfterFormCheckRef.current = shouldResume;
        if (shouldResume) {
            pauseTimer();
        }
        setIsFormCheckOpen(true);
    }, [addToast, canShowFormCheck, formCheckBlocked, formCheckQuotaMessage, pauseTimer, status]);

    const handleFormCheckClose = useCallback(() => {
        setIsFormCheckOpen(false);
        if (resumeAfterFormCheckRef.current) {
            resumeTimer();
        }
        resumeAfterFormCheckRef.current = false;
    }, [resumeTimer]);

    const handleOpenTracker = useCallback(() => {
        const shouldResume = status === 'running';
        resumeAfterTrackerRef.current = shouldResume;
        if (shouldResume) {
            pauseTimer();
        }
        setIsTrackerOpen(true);
    }, [pauseTimer, status]);

    const handleCloseTracker = useCallback(() => {
        setIsTrackerOpen(false);
        if (resumeAfterTrackerRef.current) {
            resumeTimer();
        }
        resumeAfterTrackerRef.current = false;
    }, [resumeTimer]);

    const handleResume = useCallback(() => {
        resumeTimer();
        requestWakeLock().then((ok) => setWakeLockFailed(!ok));
    }, [requestWakeLock, resumeTimer]);

    const handlePause = useCallback(() => {
        pauseTimer();
        releaseWakeLock();
    }, [pauseTimer, releaseWakeLock]);

    const canSkip = useMemo(() => {
        if (!currentPhase) return false;
        const timeElapsed = currentPhase.duration - timeRemaining;
        return timeElapsed >= currentPhase.duration / 2;
    }, [currentPhase, timeRemaining]);

    // Acquire wake lock when running; release otherwise.
    useEffect(() => {
        const shouldHoldWakeLock = isOpen && status === 'running' && !document.hidden;
        if (shouldHoldWakeLock) {
            requestWakeLock().then((ok) => setWakeLockFailed(!ok));
        } else {
            releaseWakeLock();
        }
    }, [isOpen, status, requestWakeLock, releaseWakeLock]);

    // Re-request wake lock when returning to visible, respecting pause-on-hidden logic.
    useEffect(() => {
        const handleVisibility = () => {
            if (document.hidden) {
                releaseWakeLock();
            } else if (isOpen && status === 'running') {
                requestWakeLock().then((ok) => setWakeLockFailed(!ok));
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [isOpen, status, requestWakeLock, releaseWakeLock]);

    if (!isOpen || !quest) return null;

    return (
        <>
            <ImageKitProvider urlEndpoint={config.imagekit.urlEndpoint}>
                <div className="fixed inset-0 z-50 overflow-hidden bg-slate-950 text-white" role="dialog" aria-modal="true">
                    <div className="absolute inset-0">
                        {currentPhase?.type === 'rest' ? (
                            <RestPhaseBackground />
                        ) : canUsePhaseVideo ? (
                            <video
                                data-testid="timer-phase-video"
                                src={getMediaUrl(phaseExerciseVideo, config.media.workerBase)}
                                poster={phaseExercisePoster ? getMediaUrl(phaseExercisePoster, config.media.workerBase) : undefined}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                onLoadedMetadata={handlePhaseVideoLoadedMetadata}
                                onError={() => setPhaseVideoFailed(true)}
                                className={`h-full w-full bg-slate-950 ${phaseVideoFitClass}`}
                            />
                        ) : phaseImageSrc ? (
                            <img
                                src={phaseImageSrc}
                                alt={currentPhase?.exerciseName || quest.name}
                                onLoad={handlePhaseImageLoad}
                                onError={handlePhaseImageError}
                                className={`h-full w-full bg-slate-950 ${phaseImageFitClass}`}
                            />
                        ) : (
                            <div className="h-full w-full bg-slate-900">
                                <MediaUnavailablePlaceholder />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-slate-950/35 to-slate-950/95" />
                        <div className="absolute inset-0 bg-slate-950/20" />
                    </div>

                    <div className={`relative z-10 flex min-h-[100dvh] flex-col px-4 pb-5 pt-5 transition ${isFormCheckOpen ? 'pointer-events-none blur-[1px] opacity-40' : ''} sm:px-8`}>
                        <div className="flex items-center justify-between">
                            <button
                                type="button"
                                onClick={handleClose}
                                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-slate-950/45 text-white shadow-xl backdrop-blur-xl transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
                                aria-label={t('timerSession.backToVerification', 'Back to verification')}
                            >
                                <ArrowLeft className="h-6 w-6" />
                            </button>
                            <span className="rounded-full border border-white/15 bg-slate-950/45 px-4 py-2 text-xs font-black uppercase tracking-[0.25em] text-cyan-100/80 backdrop-blur-xl">
                                {currentPhase?.type === 'rest' ? t('timerSession.recovery', 'Recovery') : quest.type}
                            </span>
                        </div>

                        {(wakeLockFailed || wakeLockErrorMessage) && (
                            <div className="mx-auto mt-4 w-full max-w-3xl rounded-xl border border-amber-300/50 bg-amber-950/70 px-4 py-3 text-sm font-semibold text-amber-100 backdrop-blur-xl">
                                {t('timerSession.wakeLockUnavailable', 'Screen wake lock unavailable; your device may sleep during the timer.')}
                            </div>
                        )}

                        {isSubmitting ? (
                            <div className="flex flex-1 flex-col items-center justify-center text-center">
                                <div className="h-16 w-16 animate-spin rounded-full border-b-2 border-t-2 border-cyan-300" />
                                <p className="mt-5 text-lg font-bold">{t('timerSession.submittingVerification', 'Submitting verification...')}</p>
                            </div>
                        ) : error ? (
                            <div className="mx-auto flex flex-1 max-w-md flex-col items-center justify-center text-center">
                                <p className="text-lg font-bold text-red-300">{t('timerSession.errorOccurred', 'An error occurred')}</p>
                                <p className="mt-2 text-sm text-slate-200">{error}</p>
                                <button
                                    type="button"
                                    onClick={resetTimer}
                                    className="mt-5 rounded-xl bg-cyan-400 px-6 py-3 text-sm font-black text-slate-950 transition hover:bg-cyan-300"
                                >
                                    {t('timerSession.tryAgain', 'Try Again')}
                                </button>
                            </div>
                        ) : currentPhase ? (
                            <>
                                <div className="mx-auto mt-8 grid w-full max-w-4xl grid-cols-3 gap-2 sm:mt-10 sm:gap-4">
                                    <TimerStatBox label={t('timerSession.statSet', 'Set')} value={currentSetLabel} />
                                    <TimerStatBox label={t('timerSession.statTimer', 'Timer')} value={formatTime(timeRemaining)} />
                                    <TimerStatBox label={t('timerSession.statTarget', 'Target')} value={targetRepsLabel} />
                                </div>

                                <div className="flex-1" />

                                <div className="mx-auto w-full max-w-3xl space-y-4">
                                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                        {status === 'running' ? (
                                            <button
                                                type="button"
                                                onClick={handlePause}
                                                className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl bg-amber-400 px-3 py-3 text-sm font-black text-slate-950 shadow-xl transition hover:bg-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-100"
                                            >
                                                <Pause className="h-5 w-5" />
                                                {t('timerSession.pause', 'Pause')}
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={handleResume}
                                                className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl bg-cyan-400 px-3 py-3 text-sm font-black text-slate-950 shadow-xl transition hover:bg-cyan-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-100"
                                            >
                                                <Play className="h-5 w-5" />
                                                {t('timerSession.resume', 'Resume')}
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={handleOpenTracker}
                                            className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-3 py-3 text-sm font-black text-white shadow-xl backdrop-blur-xl transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
                                        >
                                            <ClipboardList className="h-5 w-5" />
                                            {t('timerSession.tracker', 'Tracker')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={skipPhase}
                                            disabled={!canSkip}
                                            className="inline-flex min-h-[54px] items-center justify-center gap-2 rounded-xl border border-white/15 bg-slate-950/55 px-3 py-3 text-sm font-black text-white shadow-xl backdrop-blur-xl transition hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 disabled:cursor-not-allowed disabled:opacity-45"
                                        >
                                            <SkipForward className="h-5 w-5" />
                                            {t('timerSession.skip', 'Skip')}
                                        </button>
                                    </div>

                                    <div className="text-center drop-shadow-2xl">
                                        <h1 className="text-3xl font-black tracking-tight text-white sm:text-5xl">{exerciseTitle}</h1>
                                        <p className="mx-auto mt-2 line-clamp-3 max-w-2xl text-sm font-medium leading-6 text-slate-100/85 sm:text-base">
                                            {descriptionText}
                                        </p>
                                        {phaseMetrics && (
                                            <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-100/80">
                                                {phaseMetrics.suggestedWeight} - {t('timerSession.restLabel', 'Rest {value}', { value: phaseMetrics.rest })}
                                            </p>
                                        )}
                                    </div>

                                    {canShowFormCheck && (
                                        <div className="text-center">
                                            <button
                                                type="button"
                                                onClick={handleOpenFormCheck}
                                                disabled={formCheckBlocked}
                                                className="rounded-xl border border-cyan-200/30 bg-cyan-300/15 px-5 py-2.5 text-xs font-black uppercase tracking-[0.2em] text-cyan-100 backdrop-blur-xl transition hover:bg-cyan-300/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                {t('timerSession.startFormCheck', 'Start Form Check')}
                                            </button>
                                            {formCheckBlocked && (
                                                <p className="mt-2 text-xs font-medium text-amber-200">
                                                    {formCheckQuotaMessage}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    <div className="rounded-xl border border-white/15 bg-slate-950/50 p-4 shadow-2xl backdrop-blur-xl">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-300/15 text-cyan-100">
                                                <Dumbbell className="h-5 w-5" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-cyan-100/60">{t('timerSession.upNext', 'Up Next')}</p>
                                                <p className="mt-1 truncate text-base font-black text-white">
                                                    {upNextDisplay.title}
                                                </p>
                                            </div>
                                            {upNextDisplay.badge && (
                                                <span className="rounded-lg border border-white/10 px-3 py-2 text-xs font-black text-slate-100">
                                                    {upNextDisplay.badge}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-1 items-center justify-center text-lg font-bold">{t('timerSession.loading', 'Loading timer...')}</div>
                        )}
                    </div>
                </div>
            </ImageKitProvider>
            <WorkoutTrackerSheet
                isOpen={isTrackerOpen}
                workoutPerformance={workoutPerformance}
                onUpdate={updateWorkoutPerformance}
                onClose={handleCloseTracker}
            />
            <FormCheckPanel
                isOpen={Boolean(isOpen && quest && isFormCheckOpen && canShowFormCheck)}
                onClose={handleFormCheckClose}
                exerciseName={formCheckExerciseName}
                exerciseDescription={formCheckExerciseDescription}
                onQuotaStateChange={refreshAiFeatureQuotas}
            />
        </>
    );
};

export default TimerSessionModal;
